
import React, { Fragment } from 'react'
import { compose } from 'widgets-shared/core'
import { withComponentContext } from 'widgets-shared/core'
import { withTranslations } from 'widgets-shared/core'
import { deepObjectCopy } from 'widgets-shared/utilities'
import { Grid, Typography, Button } from '@mui/material'
import { alpha } from '@mui/material/styles';
import GSSVG from 'smartstream-widgets/controls/GSSVG'
import MatchState from 'smartstream-widgets/controls/MatchState/MatchState'
import ScoreBoardSelections from './../ScoreBoardSelections/ScoreBoardSelections'
import { competitionTranslations } from 'smartstream-widgets/constants/competitions'
import { homeFirstCompetitions } from "smartstream-widgets/constants/shared"

import { withStyles, createStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { TranslationFunction } from 'smartstream-widgets/types/sharedModule/core'
import { OddsFormat, OddsComparisonGridConfig, FixtureData, CompetitionData } from './../types'
import { getDefaultTheme } from 'smartstream-widgets/utils/getDefaultTheme'

type Props = {
  classes: {
    [K in string]: string
  },
  config: OddsComparisonGridConfig,
  contentId: string,
  data: Array<FixtureData & CompetitionData>,
  oddsFormat: OddsFormat,
  showMoreHandler?: ((arg0: string) => void),
  translations: {
    [key in string]: string | number
  } & {
    showMore: (bindings: { [key in string]: string | number }) => string
  }
}

const styles = (theme: Theme) => createStyles({
  competitorName: {
    fontWeight: 700
  },
  dash: {
    backgroundColor: theme.palette.text.secondary,
    height: 1,
    width: 14
  },
  dateCaption: {
    fontSize: 9,
    marginLeft: 14,
    lineHeight: 1.4
  },
  footer: {
    height: 56,
    paddingRight: 40
  },
  gridHeaderContainer: {
    backgroundColor: theme.palette.divider,
    height: 34,
    left: 0,
    position: 'sticky'
  },
  headerLabel: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginLeft: 14,
    marginRight: 10
  },
  scoreBoardRow: {
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:nth-child(even)': {
      backgroundColor: theme.palette.background.default,
      '& $verticalScoreBoard::after': {
        background: `linear-gradient(to right, ${theme.palette.background.default}, ${alpha(theme.palette.background.default, 0)})`
      }
    }
  },
  showMore: {
    color: theme.palette.primary.main,
    display: 'flex',
    left: 0,
    position: 'sticky',
    top: 0,
    width: '100%',
    textTransform: 'none'
  },
  verticalScoreBoard: {
    '&::after': {
      background: `linear-gradient(to right, ${theme.palette.background.paper}, ${alpha(theme.palette.background.paper, 0)})`,
      content: '\'\'',
      display: 'block',
      height: '100%',
      position: 'absolute',
      right: -16,
      top: 0,
      width: 15,
    }
  },
  competitionName: {
    fontWeight: 700
  }
})

function FixturesList(props: Props) {
  const { data, config, oddsFormat, classes, contentId, translations, showMoreHandler } = props
  const { oddsComparisonGridBookmakersId, competitionsDisplayRoundName = [], BASE_ASSET_PATH } = config
  const isByCompetition = contentId === 'today' || data[0] && data[0].fixtures
  const isBySelection = contentId === '635'
  const shouldGroupByRound = competitionsDisplayRoundName.includes(contentId)
  const showMoreIconPath = `${BASE_ASSET_PATH}/img/icons.svg#ic-chevron-right`
  if (isByCompetition) {
    return data.map(competition => {
      const { id, round, fixtures, showMore } = competition
      const competitionTranslation = translations[id]
      const competitionName = (competitionTranslation && competitionTranslation.toString()) || ''
      const showMoreLabel = translations.showMore({ competition: competitionName })

      return (
        <Fragment key={`${id}-${round}`}>
          <Grid alignItems='center' className={classes.gridHeaderContainer} container data-testid="by-competition-container">
            <Typography className={`${classes.headerLabel} ${classes.competitionName}`}>
              {competitionName}
            </Typography>
          </Grid>
          {fixtures.map(fixture => (
            <ScoreBoardSelections
              data={fixture}
              bookmakers={oddsComparisonGridBookmakersId}
              isAwayTeamFirst={!homeFirstCompetitions.includes(id)}
              oddsFormat={oddsFormat}
              classes={{ root: classes.scoreBoardRow, scoreboard: classes.verticalScoreBoard }}
              key={fixture.fixtureId || fixture.name}
            />
          ))}
          {showMore && showMoreHandler &&
            <Button
              classes={{ root: classes.showMore }}
              className={''}
              data-ht-category='navigation'
              data-ht-action={`go to ${competitionTranslation} odds`}
              data-ht-label={showMoreLabel}
              endIcon={<GSSVG xlinkHref={showMoreIconPath} />}
              onClick={() => showMoreHandler(id)}
            >
              {showMoreLabel}
            </Button>}
        </Fragment>
      )
    })
  }

  if (isBySelection) {
    return data.map(fixture => {
      const {
        competitors,
        fixtureId,
        matchSummaries,
        name,
        scheduledStartTime
      } = fixture
      const singleSelections = competitors.reduce((acc: Array<FixtureData & CompetitionData>, competitor) => {
        const fixtureWithSingleCompetitor = {
          ...deepObjectCopy(fixture) as FixtureData & CompetitionData,
          competitors: [competitor]
        }
        acc.push(fixtureWithSingleCompetitor)
        return acc
      }, [])
      return (
        <Fragment key={fixtureId}>
          <Grid container
            alignItems='flex-start'
            className={classes.gridHeaderContainer}
            direction='column'
            justifyContent='center'
            data-testid='by-selection-container'
          >
            <Typography className={`${classes.headerLabel} ${classes.competitorName}`}>
              {name}
            </Typography>
            <MatchState
              colorInverted
              classes={{ dateCaption: classes.dateCaption }}
              matchPhase={matchSummaries && matchSummaries.matchStatus}
              scheduledStartTime={scheduledStartTime}
            />
          </Grid>
          {
            singleSelections.map((fixture, index) => (
              <ScoreBoardSelections
                data={fixture}
                position={index + 1}
                hideStartTime
                showCompetitorPerRow
                bookmakers={oddsComparisonGridBookmakersId}
                oddsFormat={oddsFormat}
                classes={{ root: classes.scoreBoardRow, scoreboard: classes.verticalScoreBoard }}
                key={`${fixture.fixtureId}-${fixture.competitors[0].competitorId}`}
              />))
          }
        </Fragment>
      )
    })
  }

  let actualRound = ''
  return data.map(fixture => {
    const { roundName, name, fixtureId, competitionId } = fixture
    const shouldPlaceRound = roundName && shouldGroupByRound && (actualRound !== roundName)
    if (shouldPlaceRound) actualRound = roundName

    return (
      <Fragment key={fixtureId || name}>
        {shouldPlaceRound &&
          <Grid alignItems='center' className={classes.gridHeaderContainer} container data-testid="round-name-container">
            <Typography className={classes.headerLabel}>{roundName}</Typography>
          </Grid>
        }
        <ScoreBoardSelections
          data={fixture}
          isAwayTeamFirst={!homeFirstCompetitions.includes(competitionId)}
          oddsFormat={oddsFormat}
          bookmakers={oddsComparisonGridBookmakersId}
          classes={{ root: classes.scoreBoardRow }}
        />
      </Fragment>
    )
  })
}

export default compose(
  withComponentContext,
  withStyles(styles, { name: 'FixturesList', defaultTheme: getDefaultTheme }),
  withTranslations((t: TranslationFunction) => {
    return {
      ...competitionTranslations(t),
      ...{
        showMore: (bindings: { [key in string]: string | number }) => t('more.translation', {
          defaultValue: 'More {{competition}} games',
          context: 'Text to display on show more today button, receiving a competition as parameter, inside Odds Comparison Grid widget',
          replace: bindings
        }
        )
      }
    }
  })
)(FixturesList)
